import { GameMode, LevelProgressState, RunKind } from '../types';
import { MODE_LABELS } from './modes';

const GROWTH_RATE = 1.1;
const BASE_CORRECT_EXP = 50;
const BASE_LEVEL_REQUIREMENT = 100;
const DAILY_MULTIPLIER = 5;

/**
 * Returns the exp required to advance from the given level to the next.
 * Scaling uses a gentle exponential curve so progress remains attainable.
 */
export function expRequiredForLevel(level: number): number {
  const safeLevel = Math.max(1, level);
  return Math.round(BASE_LEVEL_REQUIREMENT * Math.pow(GROWTH_RATE, safeLevel - 1));
}

/**
 * Calculates the experience earned for a streak of correct guesses in a run.
 * Uses a geometric sum so longer streaks meaningfully reward the player.
 */
export function expForStreak(streak: number, mode: GameMode, runKind: RunKind): number {
  const cappedStreak = Math.max(0, Math.floor(streak));
  if (cappedStreak === 0) return 0;
  const seriesSum = BASE_CORRECT_EXP * ((Math.pow(GROWTH_RATE, cappedStreak) - 1) / (GROWTH_RATE - 1));
  let multiplier = 1;
  if (mode === 'daily' || runKind === 'daily') {
    multiplier = DAILY_MULTIPLIER;
  } else if (runKind === 'seeded') {
    multiplier = 0.1;
  } else if (runKind === 'regional') {
    multiplier = 0.5;
  }
  return Math.round(seriesSum * multiplier);
}

/**
 * Converts a total experience value into the player's level and progress.
 */
export function getLevelProgress(totalExp: number): LevelProgressState {
  let remaining = Math.max(0, Math.floor(totalExp));
  let level = 1;
  let requirement = expRequiredForLevel(level);

  while (remaining >= requirement) {
    remaining -= requirement;
    level += 1;
    requirement = expRequiredForLevel(level);
  }

  const progress = requirement === 0 ? 0 : Math.min(1, remaining / requirement);
  return {
    level,
    expIntoLevel: remaining,
    expForNext: requirement,
    progress,
  };
}

export function describeGain(streak: number, mode: GameMode, runKind: RunKind): string {
  const gain = expForStreak(streak, mode, runKind);
  const label = MODE_LABELS[mode] ?? mode;
  return `+${gain.toLocaleString()} XP (${label})`;
}
